/*
PURPOSE: Per-page <head> for the public marketing pages + the build-time
prerender readiness signal. Wraps MetaHead with the page's own title /
description / canonical (overriding App.tsx's generic default), and stamps
`data-prerender-ready` on <html> once the page has mounted so the Playwright
prerender crawl knows the real content (not the auth spinner) is on screen.
USE CASES:
- Rendered once near the top of each in-scope public page (Home, Manifesto,
  Principles, ForArtists, About, Trust, Contact, Terms, Privacy, ArtistTerms).
DOMAIN BOUNDARIES:
- Presentation/meta only. Canonical always points at the production domain so
  dev/stg snapshots don't compete with www for indexing.
- The readiness marker is consumed only by frontend/scripts/prerender.mjs; it is
  harmless in shipped HTML and is removed on unmount for SPA navigations.
*/

import React, { useEffect } from 'react';
import MetaHead from './MetaHead';

const SITE_URL = 'https://www.wearecoral.org';

interface PublicPageMetaProps {
  title: string;
  description: string;
  /** Canonical path, e.g. "/manifesto" or "/" for home. */
  path: string;
}

const PublicPageMeta: React.FC<PublicPageMetaProps> = ({ title, description, path }) => {
  useEffect(() => {
    document.documentElement.setAttribute('data-prerender-ready', 'true');
    return () => {
      document.documentElement.removeAttribute('data-prerender-ready');
    };
  }, []);

  const canonical = path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`;

  return <MetaHead title={title} description={description} url={canonical} />;
};

export default PublicPageMeta;
